import Swiper from 'swiper';
import { Navigation, Pagination, A11y, Autoplay, Controller, Grid } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/grid';

const BREAKPOINT_ORDER = [
  ['sm', 0],
  ['md', 768],
  ['lg', 1081],
  ['xl', 1280],
  ['2xl', 1470],
];

function parseSlidesPerView(el, defaultJson) {
  try {
    return JSON.parse(el.dataset.slidesPerView || defaultJson);
  } catch {
    return JSON.parse(defaultJson);
  }
}


function parseSpaceBetween(el, defaultJson) {
  try {
    return JSON.parse(el.dataset.spaceBetween || defaultJson);
  } catch {
    return JSON.parse(defaultJson);
  }
}

function parseBooleanOption(el, key, defaultValue) {
  const attr = el.dataset[key];
  if (attr === undefined) return defaultValue;

  const value = attr.trim().toLowerCase();
  if (value === '' || value === 'true' || value === '1' || value === 'yes' || value === 'on') return true;
  if (value === 'false' || value === '0' || value === 'no' || value === 'off') return false;
  return defaultValue;
}

function normalizeGridRows(value) {
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return null;
    return Math.max(1, Math.round(value));
  }
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) return null;
  return Math.max(1, Math.round(parsed));
}

function parseGridRows(el, defaultValue = 1) {
  const perBreakpoint = {};
  let base = normalizeGridRows(defaultValue) ?? 1;
  const attr = el.dataset.gridRows;

  if (attr) {
    const simple = normalizeGridRows(attr);
    if (simple) {
      base = simple;
    } else {
      try {
        const parsed = JSON.parse(attr);
        if (typeof parsed === 'number' || typeof parsed === 'string') {
          const rows = normalizeGridRows(parsed);
          if (rows) base = rows;
        } else if (parsed && typeof parsed === 'object') {
          const { default: defaultRows, ...others } = parsed;
          const parsedDefault = normalizeGridRows(defaultRows);
          if (parsedDefault) base = parsedDefault;
          Object.entries(others).forEach(([key, value]) => {
            const rows = normalizeGridRows(value);
            if (rows) perBreakpoint[key] = rows;
          });
        }
      } catch {
        // ignore JSON parsing errors, fall back to default
      }
    }
  }

  BREAKPOINT_ORDER.forEach(([key]) => {
    const datasetKey = `gridRows${key.charAt(0).toUpperCase()}${key.slice(1)}`;
    if (el.dataset[datasetKey] !== undefined) {
      const rows = normalizeGridRows(el.dataset[datasetKey]);
      if (rows) perBreakpoint[key] = rows;
    }
  });

  return { base, breakpoints: perBreakpoint };
}

const parseAutoplay = (el, defaultValue) => parseBooleanOption(el, 'autoplay', defaultValue);
const parseLoop = (el, defaultValue) => parseBooleanOption(el, 'loop', defaultValue);
const parseAutoHeight = (el, defaultValue) => parseBooleanOption(el, 'autoHeight', defaultValue);

function resolvePagination(el, root) {
  return el.querySelector('.swiper-pagination') || root?.querySelector('.swiper-pagination');
}

function resolveNavigation(root, prevSelector = '.swiper-button-prev', nextSelector = '.swiper-button-next') {
  const prev = root?.querySelector(prevSelector);
  const next = root?.querySelector(nextSelector);
  if (!prev || !next) return undefined;
  return { prevEl: prev, nextEl: next };
}

function buildBreakpointConfig(slides = {}, spaces = {}, extras = {}) {
  const result = {};
  BREAKPOINT_ORDER.forEach(([key, width]) => {
    const config = { ...extras[key] };
    if (slides[key] !== undefined) config.slidesPerView = slides[key];
    if (spaces[key] !== undefined) config.spaceBetween = spaces[key];
    if (Object.keys(config).length > 0) result[width] = config;
  });
  return result;
}

function mergeBreakpointExtras(...extrasList) {
  return extrasList.reduce((acc, extras = {}) => {
    Object.entries(extras).forEach(([key, value]) => {
      if (!acc[key]) acc[key] = {};
      Object.assign(acc[key], value);
    });
    return acc;
  }, {});
}

function getEnabledBreakpointExtras(el) {
  const enabledFrom = (el.dataset.enabledFrom || '').trim();
  const enabledIndex = BREAKPOINT_ORDER.findIndex(([key]) => key === enabledFrom);
  if (enabledIndex < 0) return {};

  return BREAKPOINT_ORDER.reduce((extras, [key], index) => {
    extras[key] = { enabled: index >= enabledIndex };
    return extras;
  }, {});
}

const SWIPER_TYPES = [
  {
    selector: '.carthago-interieur__grid-swiper',
    modules: [Navigation, A11y],
    defaults: {
      slides: { '2xl': 'auto', xl: 'auto', lg: 'auto', md: 'auto', sm: 1 },
      spaces: { '2xl': 56, xl: 56, lg: 56, md: 40, sm: 0 },
      autoplay: false,
      loop: () => false,
    },
    baseOptions: (el, root) => ({
      watchOverflow: true,
      roundLengths: false,
      navigation: resolveNavigation(root),
      observer: true,
      observeParents: true,
      resizeObserver: true,
    }),
  },
  {
    selector: '.grid-swiper',
    modules: [Pagination, Navigation, A11y, Autoplay, Grid],
    defaults: {
      slides: { '2xl': 4, xl: 4, lg: 3, md: 2, sm: 1 },
      spaces: { '2xl': 28, xl: 28, lg: 28, md: 24, sm: 16 },
      autoplay: false,
      autoplayDelay: 6000,
      loop: () => false,
    },
    baseOptions: (el, root) => ({
      watchOverflow: true,
      roundLengths: true,
      pagination: { el: resolvePagination(el, root), type: 'progressbar' },
    }),
  },
  {
    selector: '.hero-swiper',
    modules: [Pagination, Navigation, A11y, Autoplay, Grid],
    defaults: {
      slides: { '2xl': 1.48, xl: 1.48, md: 1.30, sm: 1 },
      spaces: { '2xl': 36, xl: 36, md: 24, sm: 16 },
      autoplay: true,
      autoplayDelay: 6000,
      loop: (count) => count >= 3,
    },
    baseOptions: (el, root) => ({
      initialSlide: 0,
      centeredSlides: true,
      centeredSlidesBounds: true,
      navigation: resolveNavigation(root),
      pagination: { el: resolvePagination(el, root), type: 'progressbar' },
    }),
  },
  {
    selector: '.image-swiper',
    modules: [Pagination, Navigation, A11y, Autoplay, Grid],
    defaults: {
      slides: { '2xl': 1, xl: 1, md: 1, sm: 1 },
      spaces: { '2xl': 36, xl: 36, md: 24, sm: 16 },
      autoplay: true,
      autoplayDelay: 6000,
      loop: (count) => count >= 2,
    },
    baseOptions: (el, root) => ({
      navigation: resolveNavigation(root),
      pagination: { el: resolvePagination(el, root), type: 'progressbar' },
    }),
  },
  {
    selector: '.carousel-swiper',
    modules: [Pagination, Navigation, A11y, Autoplay, Grid],
    defaults: {
      slides: { '2xl': 3, xl: 3, md: 2, sm: 1 },
      spaces: { '2xl': 36, xl: 30, md: 24, sm: 16 },
      autoplay: true,
      autoplayDelay: 6000,
      loop: (count) => count >= 3,
    },
    baseOptions: (el, root) => ({
      navigation: resolveNavigation(root),
      pagination: { el: resolvePagination(el, root), type: 'progressbar' },
    }),
  },
];

let navigationWatcherConfigured = false;
let baureihengridWatcherConfigured = false;

const NAVIGATION_SWIPER_CONFIG = {
  modules: [Pagination, A11y, Autoplay, Grid],
  defaults: {
    slides: { '2xl': 1, xl: 1, lg: 1.06, md: 1.02, sm: 1.1 },
    spaces: { '2xl': 0, xl: 0, lg: 24, md: 16, sm: 8 },
    autoplay: false,
    autoplayDelay: 6000,
    loop: false,
  },
  baseOptions: (el, root) => ({
    watchOverflow: true,
    roundLengths: true,
    pagination: { el: resolvePagination(el, root), type: 'progressbar' },
    slidesOffsetBefore: 0,
  }),
  breakpointExtras: (el) => {
    const width = el.clientWidth || el.offsetWidth || 0;
    const offset = Math.max(Math.round(width * 0.1), 40);
    return {
      sm: { slidesOffsetAfter: 0, allowTouchMove: true },
      md: { slidesOffsetAfter: offset, allowTouchMove: true },
      lg: { slidesOffsetAfter: offset, allowTouchMove: true },
      xl: { slidesOffsetAfter: offset, allowTouchMove: false },
      '2xl': { slidesOffsetAfter: offset, allowTouchMove: false },
    };
  },
};

function ensureNavigationPagination(el) {
  const container = el.closest('.wm-model-panels');
  if (!container) return null;

  let wrapper = container.querySelector('.swiper-pagination__wrapper');
  if (!wrapper) {
    wrapper = document.createElement('div');
    wrapper.className = 'swiper-pagination__wrapper';
    const pagination = document.createElement('div');
    pagination.className = 'swiper-pagination';
    wrapper.appendChild(pagination);
    container.insertBefore(wrapper, el);
    return pagination;
  }

  let pagination = wrapper.querySelector('.swiper-pagination');
  if (!pagination) {
    pagination = document.createElement('div');
    pagination.className = 'swiper-pagination';
    wrapper.appendChild(pagination);
  }
  return pagination;
}

function initNavigationSwipers() {
  const enable = window.matchMedia('(max-width: 1279px)').matches;

  document.querySelectorAll('.navigation-swiper').forEach((el) => {
    const state = el.dataset.navSwiper || 'disabled';

    if (enable) {
      if (state === 'enabled') {
        if (!el.classList.contains('swiper')) el.classList.add('swiper');
        ensureNavigationPagination(el);
        return;
      }

      el.classList.add('swiper');
      ensureNavigationPagination(el);
      initConfiguredSwiper(el, NAVIGATION_SWIPER_CONFIG);
      el.dataset.navSwiper = 'enabled';
    } else if (state === 'enabled') {
      const swiper = el.swiper;
      if (swiper) swiper.destroy(true, true);
      delete el.dataset.swiperInit;
      el.dataset.navSwiper = 'disabled';
      el.classList.remove('swiper');
      const wrapper = el.querySelector('.swiper-wrapper');
      wrapper?.removeAttribute('style');
      el.querySelectorAll('.swiper-slide').forEach((slide) => {
        slide.removeAttribute('style');
      });

      const container = el.closest('.wm-model-panels');
      const paginationWrapper = container?.querySelector('.swiper-pagination__wrapper');
      if (paginationWrapper) {
        paginationWrapper.remove();
      }
    }
  });
}

function setupNavigationSwiperWatcher() {
  const mediaQuery = window.matchMedia('(max-width: 1279px)');
  const handler = () => initNavigationSwipers();
  if (typeof mediaQuery.addEventListener === 'function') {
    mediaQuery.addEventListener('change', handler);
  } else if ('onchange' in mediaQuery) {
    mediaQuery.onchange = handler;
  }

  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(initNavigationSwipers, 150);
  });
}

const BAUREIHENGRID_SWIPER_CONFIG = {
  modules: [Pagination, Navigation, A11y, Autoplay, Grid],
  defaults: {
    slides: { sm: 1 },
    spaces: { sm: 16 },
    autoplay: false,
    autoplayDelay: 6000,
    loop: false,
  },
  baseOptions: (el, root) => ({
    watchOverflow: true,
    roundLengths: true,
    navigation: resolveNavigation(root),
    pagination: { el: resolvePagination(el, root), type: 'progressbar' },
  }),
};

function initBaureihengridSwipers() {
  const enable = window.matchMedia('(max-width: 767px)').matches;

  document.querySelectorAll('[data-baureihengrid-mobile-swiper="true"]').forEach((el) => {
    const state = el.dataset.baureihengridSwiper || 'disabled';

    if (enable) {
      if (state === 'enabled') {
        if (!el.classList.contains('swiper')) el.classList.add('swiper');
        return;
      }

      el.classList.add('swiper');
      initConfiguredSwiper(el, BAUREIHENGRID_SWIPER_CONFIG);
      el.dataset.baureihengridSwiper = 'enabled';
    } else if (state === 'enabled') {
      const swiper = el.swiper;
      if (swiper) swiper.destroy(true, true);
      delete el.dataset.swiperInit;
      el.dataset.baureihengridSwiper = 'disabled';
      el.classList.remove('swiper');
      const wrapper = el.querySelector('.swiper-wrapper');
      wrapper?.removeAttribute('style');
      el.querySelectorAll('.swiper-slide').forEach((slide) => {
        slide.removeAttribute('style');
      });
    }
  });
}

function setupBaureihengridSwiperWatcher() {
  const mediaQuery = window.matchMedia('(max-width: 767px)');
  const handler = () => initBaureihengridSwipers();
  if (typeof mediaQuery.addEventListener === 'function') {
    mediaQuery.addEventListener('change', handler);
  } else if ('onchange' in mediaQuery) {
    mediaQuery.onchange = handler;
  }

  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(initBaureihengridSwipers, 150);
  });
}

function initConfiguredSwiper(el, config) {
  if (el.dataset.swiperInit === 'true') return;
  el.dataset.swiperInit = 'true';

  const root = el.parentElement;
  const slides = parseSlidesPerView(el, JSON.stringify(config.defaults.slides));
  const spaces = parseSpaceBetween(el, JSON.stringify(config.defaults.spaces));
  const slideCount = el.querySelectorAll('.swiper-slide').length;

  const autoplayEnabled = parseAutoplay(el, config.defaults.autoplay);
  const autoplayConfig = autoplayEnabled ? { delay: config.defaults.autoplayDelay ?? 4000, disableOnInteraction: false } : false;
  const loopDefault = typeof config.defaults.loop === 'function' ? config.defaults.loop(slideCount) : config.defaults.loop;
  const loopEnabled = parseLoop(el, loopDefault);

  const baseOptions = config.baseOptions ? config.baseOptions(el, root) : {};
  const fallbackGridRows = baseOptions.grid?.rows ?? config.defaults.gridRows ?? 1;
  const gridRowsConfig = parseGridRows(el, fallbackGridRows);
  const gridFill = baseOptions.grid?.fill ?? config.defaults.gridFill ?? 'row';

  const hasGridOverrides = Object.keys(gridRowsConfig.breakpoints).length > 0;
  const gridBreakpointExtras = {};
  if (hasGridOverrides) {
    BREAKPOINT_ORDER.forEach(([key]) => {
      const rows = gridRowsConfig.breakpoints[key] ?? gridRowsConfig.base;
      gridBreakpointExtras[key] = { grid: { rows, fill: gridFill } };
    });
  }

  const autoHeightDefault =
    (typeof baseOptions.autoHeight === 'boolean' ? baseOptions.autoHeight : undefined) ??
    (typeof config.defaults.autoHeight === 'boolean' ? config.defaults.autoHeight : undefined);
  const autoHeightEnabled =
    autoHeightDefault !== undefined ? parseAutoHeight(el, autoHeightDefault) : parseAutoHeight(el, false);

  const breakpoints = buildBreakpointConfig(
    slides,
    spaces,
    mergeBreakpointExtras(config.breakpointExtras?.(el, root), gridBreakpointExtras, getEnabledBreakpointExtras(el)),
  );

  const options = {
    modules: config.modules,
    autoplay: autoplayConfig,
    loop: loopEnabled,
    speed: config.speed ?? 1200,
    breakpoints,
    ...baseOptions,
  };
  if ((el.dataset.enabledFrom || '').trim()) {
    options.enabled = false;
  }
  options.autoHeight = autoHeightEnabled;

  const hasGridRows =
    gridRowsConfig.base > 1 ||
    Object.keys(gridRowsConfig.breakpoints).length > 0 ||
    (baseOptions.grid && baseOptions.grid.rows !== undefined);
  if (hasGridRows) {
    options.grid = {
      ...(options.grid ?? {}),
      fill: options.grid?.fill ?? gridFill,
      rows: gridRowsConfig.base,
    };
  }

  const isGlobalDebug = typeof window !== 'undefined' && window.SWIPER_DEBUG === true;
  const debugEnabled = parseBooleanOption(el, 'swiperDebug', false) || isGlobalDebug;
  if (debugEnabled && el.dataset.swiperDebugged !== 'true') {
    el.dataset.swiperDebugged = 'true';
    const label = el.id ? `#${el.id}` : el.className ? `.${Array.from(el.classList).join('.')}` : el.tagName.toLowerCase();
    // eslint-disable-next-line no-console
    console.groupCollapsed(`[Swiper Debug] ${label}`);
    // eslint-disable-next-line no-console
    console.log('Element:', el);
    // eslint-disable-next-line no-console
    console.log('Options:', options);
    // eslint-disable-next-line no-console
    console.log('Breakpoints:', options.breakpoints);
    console.groupEnd();
  }

  if (!options.pagination) {
    const paginationEl = resolvePagination(el, root);
    if (paginationEl) options.pagination = { el: paginationEl, type: 'progressbar' };
  }

  if (!options.navigation && config.modules.includes(Navigation)) {
    const nav = resolveNavigation(root);
    if (nav) options.navigation = nav;
  }

  const swiper = new Swiper(el, options);
}

export default function initSwiperSlider() {
  SWIPER_TYPES.forEach((config) => {
    document.querySelectorAll(config.selector).forEach((el) => {
		if (el.dataset.swiperController === 'editor') return;
		if (config.selector === '.image-swiper' && el.classList.contains('carthago-interieur__grid-swiper')) return;
      initConfiguredSwiper(el, config);
    });
  });

  initNavigationSwipers();
  if (!navigationWatcherConfigured) {
    setupNavigationSwiperWatcher();
    navigationWatcherConfigured = true;
  }
  initBaureihengridSwipers();
  if (!baureihengridWatcherConfigured) {
    setupBaureihengridSwiperWatcher();
    baureihengridWatcherConfigured = true;
  }

  document.querySelectorAll('.timeline-swiper').forEach((el) => {
    if (el.dataset.swiperInit === 'true') return;
    el.dataset.swiperInit = 'true';

    const root = el.parentElement;
    const sb = parseSpaceBetween(el, '{"2xl":60,"xl":60,"md":30,"sm":20}');
    const yearsPrev = root.querySelector('.timeline-years-prev');
    const yearsNext = root.querySelector('.timeline-years-next');
    const yearsContainer = root.querySelector('.timeline-years'); // <div class="timeline-years"></div>
    const progressEl = root.querySelector('.timeline-years-pagination .swiper-pagination');
    const progressWrapper = root.querySelector('.timeline-years-pagination');
    const timelineUid = el.dataset.timelineUid || `timeline-${Math.random().toString(36).slice(2, 10)}`;
    const tablistLabel = yearsContainer?.dataset.tablistLabel || 'Jahre der Zeitlinie';
    const yearLabelTemplate = yearsContainer?.dataset.yearLabelTemplate || 'Gehe zu Jahr %s';

    el.dataset.timelineUid = timelineUid;
    if (yearsContainer) {
      yearsContainer.setAttribute('role', 'tablist');
      yearsContainer.setAttribute('aria-label', tablistLabel);
    }

    // Build a nested Swiper for years if not already converted
    let yearsSwiperEl = null;
    if (yearsContainer) {
      // If it doesn't yet have Swiper structure, build it
      if (!yearsContainer.classList.contains('swiper')) {
        yearsContainer.classList.add('swiper', 'years-swiper');
        // Preserve any existing content only if empty; we rebuild anyway
        yearsContainer.innerHTML = '';
        const wrapper = document.createElement('div');
        wrapper.className = 'swiper-wrapper';
        yearsContainer.appendChild(wrapper);
      }
      yearsSwiperEl = yearsContainer; // the container becomes the Swiper element
    }

    const yearButtons = [];

    const slides = Array.from(el.querySelectorAll('.swiper-slide'));
    const slideCount = slides.length;
    const autoplayEnabled = parseAutoplay(el, false);
    const autoplayConfig = autoplayEnabled ? { delay: 4000, disableOnInteraction: false } : false;
    const loopEnabled = parseLoop(el, slideCount >= 3);
    const autoHeightEnabled = parseAutoHeight(el, true);

    // Collect years from data-year attribute on each slide (fallback to text content)
    const years = slides.map((s, i) => {
      const y = s.getAttribute('data-year') || s.dataset.year || s.textContent.trim();
      return y || String(i + 1);
    });

    slides.forEach((slide, idx) => {
      slide.id = `${timelineUid}-panel-${idx}`;
      slide.setAttribute('role', 'tabpanel');
      slide.setAttribute('aria-labelledby', `${timelineUid}-tab-${idx}`);
      slide.tabIndex = idx === 0 ? 0 : -1;
    });
    markTimelineProgressbarDecorative();

    const swiper = new Swiper(el, {
      modules: [Pagination, Navigation, A11y, Autoplay],
      // Keep loop for smoother UX if there are 3+ slides
      loop: loopEnabled,
      slidesPerView: 1,
      autoHeight: autoHeightEnabled,
      autoplay: autoplayConfig,
      speed: 600,
      pagination: {
        el: progressEl,
        type: 'progressbar',
      },
      breakpoints: {
        0: {           // Handy
          spaceBetween: sb.sm ?? 20,
        },
        768: {         // iPad/Tablet
          spaceBetween: sb.md ?? 30,
        },
        1280: {
          spaceBetween: sb.xl ?? 60,
        },
        1470: {
          spaceBetween: sb['2xl'] ?? sb.xl ?? 60,
        },
      },
      a11y: {
        enabled: true,
      },
      on: {
        // Sync custom navigation on init and slide changes
        init(sw) {
          if (!yearsContainer) return;
          // Build custom year navigation only once (clear slides, keep Swiper structure)
          const yearsWrapperInit = yearsSwiperEl ? yearsSwiperEl.querySelector('.swiper-wrapper') : null;
          if (yearsWrapperInit) {
            yearsWrapperInit.innerHTML = '';
          }
          years.forEach((year, idx) => {
            const wrapper = yearsSwiperEl ? yearsSwiperEl.querySelector('.swiper-wrapper') : null;
            if (!wrapper) return;
            const slide = document.createElement('div');
            slide.className = 'swiper-slide timeline-year-slide';

            const btn = document.createElement('button');
            btn.id = `${timelineUid}-tab-${idx}`;
            btn.setAttribute('role', 'tab');
            btn.setAttribute('aria-selected', 'false');
            btn.setAttribute('aria-controls', `${timelineUid}-panel-${idx}`);
            const label = yearLabelTemplate.replace('%s', year);
            btn.type = 'button';
            btn.className = 'timeline-year';
            btn.textContent = year;
            btn.setAttribute('aria-label', label);
            btn.setAttribute('data-index', String(idx));
            btn.addEventListener('click', () => {
              if (typeof sw.slideToLoop === 'function') {
                sw.slideToLoop(idx);
              } else {
                sw.slideTo(idx);
              }
            });
            // Keyboard support (Arrow keys, Home/End, Enter/Space)
            btn.addEventListener('keydown', (e) => {
              const btns = yearButtons;
              const currentIndex = btns.indexOf(e.currentTarget);
              if (currentIndex === -1) return;
              let targetIndex = null;
              switch (e.key) {
                case 'ArrowRight':
                case 'Right':
                  targetIndex = (currentIndex + 1) % btns.length;
                  break;
                case 'ArrowLeft':
                case 'Left':
                  targetIndex = (currentIndex - 1 + btns.length) % btns.length;
                  break;
                case 'Home':
                  targetIndex = 0;
                  break;
                case 'End':
                  targetIndex = btns.length - 1;
                  break;
                case 'Enter':
                case ' ': // Space
                  e.preventDefault();
                  e.currentTarget.click();
                  return;
                default:
                  return;
              }
              e.preventDefault();
              const targetBtn = btns[targetIndex];
              if (targetBtn) {
                targetBtn.focus();
                targetBtn.click();
              }
            });

            yearButtons.push(btn);
            slide.appendChild(btn);
            wrapper.appendChild(slide);
          });
          updateYearActive(sw);
          markTimelineProgressbarDecorative();

          // Initialize years Swiper
          let yearsSwiper = null;
          if (yearsSwiperEl) {
            yearsSwiper = new Swiper(yearsSwiperEl, {
              modules: [Navigation, A11y, Controller],
              watchOverflow: true,
              roundLengths: true,
              ...(yearsPrev && yearsNext ? { navigation: { prevEl: yearsPrev, nextEl: yearsNext } } : {}),
              slidesPerView: 'auto',
              spaceBetween: 0,
            });

            // When clicking a year (already wired), keep yearsSwiper in sync on slide changes
            sw.on('slideChange', () => {
              const target = sw.params.loop ? sw.realIndex : sw.activeIndex;
              if (yearsSwiper && yearsSwiper.slides && yearsSwiper.slides[target]) {
                yearsSwiper.slideTo(Math.max(0, target - 2));
              }
            });
          }

          const activeBtn = yearsContainer.querySelector('.timeline-year.is-active');
          centerYearButton(activeBtn);
          markTimelineProgressbarDecorative();
        },
        slideChange(sw) {
          if (!yearsContainer) return;
          updateYearActive(sw);
          const activeBtn = yearsContainer.querySelector('.timeline-year.is-active');
          centerYearButton(activeBtn);
          markTimelineProgressbarDecorative();
          // (yearsSwiper centering handled above via slideTo); fallback kept
        },
      },
    });

    function centerYearButton(btn) {
      if (!yearsContainer || !btn || yearsContainer.classList.contains('swiper-initialized')) return;
      const containerRect = yearsContainer.getBoundingClientRect();
      const btnRect = btn.getBoundingClientRect();
      const current = yearsContainer.scrollLeft;
      const delta = (btnRect.left + btnRect.width / 2) - (containerRect.left + containerRect.width / 2);
      yearsContainer.scrollTo({ left: current + delta, behavior: 'smooth' });
    }

    function scrollYearsBy(px) {
      if (!yearsContainer || yearsContainer.classList.contains('swiper-initialized')) return;
      yearsContainer.scrollBy({ left: px, behavior: 'smooth' });
    }

    function markTimelineProgressbarDecorative() {
      [progressWrapper, progressEl].forEach((progressNode) => {
        if (!progressNode) return;
        progressNode.setAttribute('aria-hidden', 'true');
        progressNode.setAttribute('role', 'presentation');
        progressNode.removeAttribute('aria-label');
        progressNode.removeAttribute('aria-valuemin');
        progressNode.removeAttribute('aria-valuemax');
        progressNode.removeAttribute('aria-valuenow');
        progressNode.removeAttribute('aria-valuetext');
      });
    }

    if (yearsContainer) {
      const active = swiper.params.loop ? swiper.realIndex : swiper.activeIndex;
      yearsContainer.querySelectorAll('.timeline-year').forEach((b, i) => {
        b.tabIndex = (i === active) ? 0 : -1;
      });
    }

    if (yearsPrev) {
      yearsPrev.addEventListener('click', () => {
        scrollYearsBy(-(yearsContainer ? Math.max(200, yearsContainer.clientWidth * 0.8) : 300));
      });
    }
    if (yearsNext) {
      yearsNext.addEventListener('click', () => {
        scrollYearsBy( yearsContainer ? Math.max(200, yearsContainer.clientWidth * 0.8) : 300);
      });
    }

    function updateYearActive(sw) {
      if (!yearsContainer) return;
      const active = sw.params.loop ? sw.realIndex : sw.activeIndex;
      slides.forEach((slide, i) => {
        const isActive = i === active;
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        slide.tabIndex = isActive ? 0 : -1;
      });
      yearsContainer.querySelectorAll('.timeline-year').forEach((b, i) => {
        if (i === active) {
          b.classList.add('is-active');
          b.setAttribute('aria-current', 'true');
          b.setAttribute('aria-selected', 'true');
          b.tabIndex = 0; // roving tabindex: only active is tabbable
          centerYearButton(b);
        } else {
          b.classList.remove('is-active');
          b.removeAttribute('aria-current');
          b.setAttribute('aria-selected', 'false');
          b.tabIndex = -1;
        }
      });
    }
  });
}
